import { differenceInYears, isValid as isValidDate } from "date-fns";
import { formatUserDateTime } from "@/utilities/datetime";
import { SELF_EXCLUDED_DATETIME } from "@/utilities/dateTimeFormats";
import { getDeviceId } from "utilities/getDeviceId";
import { fetchWithAmznWaf as protectedFetch } from "utilities/api/fetchWithAmznWaf";
import { attempt } from "@/utilities/attempt";
import { ok, err, type Result } from "@/utilities/result";

const API_URI = process.env.GATSBY_API_URI;

export type ValidationError = {
  message: string;
  messageTemplate: string;
  propertyPath: string;
  closedUntil?: string;
};

export type AuthError = {
  message: string;
  messageTemplate: string;
  statusCode?: number;
  errors?: Record<string, string>;
};

const INFRASTRUCTURE_ERROR: AuthError = {
  message: "Something went wrong. Please try again.",
  messageTemplate: "infrastructure.error",
};

type ValidationErrorResponse = {
  errors: ValidationError[];
};

type BodyErrorResponse = {
  body: {
    code?: number;
    errors: [{ message: string }, ...Array<{ message: string }>];
  };
};

const hasValidationErrors = (json: unknown): json is ValidationErrorResponse =>
  typeof json === "object" &&
  json !== null &&
  "errors" in json &&
  Array.isArray(json.errors);

const hasBodyError = (json: unknown): json is BodyErrorResponse =>
  typeof json === "object" &&
  json !== null &&
  "body" in json &&
  typeof json.body === "object" &&
  json.body !== null &&
  "errors" in json.body &&
  Array.isArray(json.body.errors) &&
  json.body.errors.length > 0;

const hasMessage = (json: unknown): json is { message: string } =>
  typeof json === "object" &&
  json !== null &&
  "message" in json &&
  typeof json.message === "string";

export const hasFieldErrors = (
  error: Pick<AuthError, "message" | "messageTemplate"> & {
    errors?: Record<string, string>;
  },
): error is AuthError & Required<Pick<AuthError, "errors">> => {
  return error.errors !== undefined && Object.keys(error.errors).length > 0;
};

export const formatAuthError = (
  json: { errors: ValidationError[] },
  statusCode: number,
) => {
  const errors = json.errors.reduce<Record<string, string>>((acc, e) => {
    if (!e.propertyPath) return acc;
    return { ...acc, [e.propertyPath]: e.message };
  }, {});

  const messageTemplate = json.errors.map((e) => e.messageTemplate).join(", ");

  const message = json.errors
    .map((e) => {
      let msg = e.message;
      if (
        e.closedUntil &&
        ["user.closed.selfExcluded", "user.closed.ntrcExcluded"].includes(
          e.messageTemplate,
        )
      ) {
        const closedUntilDate = new Date(e.closedUntil);
        if (isValidDate(closedUntilDate)) {
          const diff = differenceInYears(closedUntilDate, new Date());
          const untilString =
            diff >= 90
              ? "indefinitely"
              : `until ${formatUserDateTime(closedUntilDate, SELF_EXCLUDED_DATETIME)}`;
          msg = `Your account is self-excluded ${untilString}.`;
        }
      }
      return msg;
    })
    .join(", ");

  return { message, messageTemplate, statusCode, errors } satisfies AuthError;
};

type AuthFetchOptions = {
  method?: "GET" | "POST" | "DELETE";
  body?: Record<string, unknown>;
  token?: string | null;
  useWaf?: boolean;
};

export const authFetch = async <T>(
  endpoint: string,
  options: AuthFetchOptions = {},
): Promise<Result<T, AuthError>> => {
  if (typeof fetch === "undefined") {
    return err({
      message: "Unable to connect. Please try again.",
      messageTemplate: "network.unavailable",
    });
  }

  const { method = "GET", body, token, useWaf = false } = options;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "X-DeviceId": getDeviceId(),
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  const fetchFn = useWaf ? protectedFetch : fetch;

  const responseResult = await attempt(() =>
    fetchFn(`${API_URI}${endpoint}`, {
      method,
      mode: "cors",
      headers,
      ...(body && { body: JSON.stringify(body) }),
    }),
  );

  if (!responseResult.ok) {
    return err(INFRASTRUCTURE_ERROR);
  }

  const response = responseResult.data;

  // Handle 204 No Content
  if (response.status === 204) {
    return ok(null as T);
  }

  const jsonResult = await attempt<unknown>(response.json());

  if (!jsonResult.ok) {
    return err(INFRASTRUCTURE_ERROR);
  }

  const json = jsonResult.data;

  if (!response.ok) {
    if (hasValidationErrors(json)) {
      return err(formatAuthError(json, response.status));
    }

    if (hasMessage(json)) {
      return err({
        message: json.message,
        messageTemplate: "",
        statusCode: response.status,
      });
    }

    if (hasBodyError(json)) {
      const message = json.body.errors[0].message;
      const statusCode = json.body.code ?? response.status;
      return err({ message, messageTemplate: "", statusCode });
    }

    return err(INFRASTRUCTURE_ERROR);
  }

  return ok(json as T);
};
